import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Dropdown, Space, Badge } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  BulbOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  BellOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import inventoryService from '../../services/inventory.service';

interface HeaderProps {
  collapsed: boolean;
  toggle: () => void;
}

type MenuItem = Required<MenuProps>['items'][number];

const Header: React.FC<HeaderProps> = ({ collapsed, toggle }) => {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();

  const [lowStock, setLowStock] = useState<{ id: number; name: string; stockQuantity?: number }[]>([]);
  const [loadingLowStock, setLoadingLowStock] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchLowStock = async () => {
    try {
      setLoadingLowStock(true);
      const products = await inventoryService.getLowStockProducts();
      setLowStock(products.map(p => ({ id: p.id, name: p.name, stockQuantity: (p as any).stockQuantity })));
    } catch (e) {
      // ignore head-up errors
    } finally {
      setLoadingLowStock(false);
    }
  };

  useEffect(() => {
    fetchLowStock();
    const t = setInterval(fetchLowStock, 60000); // poll every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = () => {
      const fsElement: any = document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).mozFullScreenElement || (document as any).msFullscreenElement;
      setIsFullscreen(!!fsElement);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler as any);
    document.addEventListener('mozfullscreenchange', handler as any);
    document.addEventListener('MSFullscreenChange', handler as any);
    handler();
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler as any);
      document.removeEventListener('mozfullscreenchange', handler as any);
      document.removeEventListener('MSFullscreenChange', handler as any);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      const doc: any = document;
      const elem: any = document.documentElement;
      const fsElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement;
      if (!fsElement) {
        if (elem.requestFullscreen) await elem.requestFullscreen();
        else if (elem.webkitRequestFullscreen) await elem.webkitRequestFullscreen();
        else if (elem.mozRequestFullScreen) await elem.mozRequestFullScreen();
        else if (elem.msRequestFullscreen) await elem.msRequestFullscreen();
      } else {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
        else if (doc.mozCancelFullScreen) await doc.mozCancelFullScreen();
        else if (doc.msExitFullscreen) await doc.msExitFullscreen();
      }
    } catch (_) {
      // ignore errors
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const userMenuItems: MenuItem[] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile')
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout
    },
  ];

  const lowStockMenuItems: MenuItem[] = (
    lowStock.length === 0 ? [
      { key: 'none', label: loadingLowStock ? 'Loading…' : 'No low stock items' }
    ] : [
      ...lowStock.slice(0, 6).map(p => ({
        key: `low-${p.id}`,
        icon: <WarningOutlined style={{ color: '#faad14' }} />,
        label: `${p.name}${typeof p.stockQuantity === 'number' ? ` — Stock: ${p.stockQuantity}` : ''}`,
        onClick: () => navigate('/inventory')
      })),
      { type: 'divider' as const },
      { key: 'view-all', label: 'View all low stock', onClick: () => navigate('/inventory') }
    ]
  );
  
  return (
    <Layout.Header className={`app-header ${collapsed ? 'collapsed' : ''}`}>
      <div className="header-left">
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggle}
          className="trigger-button"
        />
      </div>
      
      <div className="header-right">
        <Dropdown menu={{ items: lowStockMenuItems }} trigger={["click"]}>
          <Badge count={lowStock.length} overflowCount={99} offset={[ -2, 2 ]}>
            <Button type="text" icon={<BellOutlined />} title="Low stock notifications" />
          </Badge>
        </Dropdown>

        <Button
          type="text"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullscreen}
          className="theme-toggle"
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        />

        <Button
          type="text"
          icon={<BulbOutlined />}
          onClick={toggleDarkMode}
          className="theme-toggle"
          title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        />
        
        <Dropdown
          menu={{ items: userMenuItems }}
          trigger={['click']}
        >
          <Space className="user-menu">
            <Button type="text" icon={<UserOutlined />}>
              {auth.user?.username || 'User'}
            </Button>
          </Space>
        </Dropdown>
      </div>
    </Layout.Header>
  );
};

export default Header; 